import { React, useEffect, useState, useRef, useMemo, useCallback } from "react";
import cogoToast from "cogo-toast";
import { Spinner, Input } from "reactstrap";
import { useHistory, Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Button } from "reactstrap";
import { AgGridReact } from "ag-grid-react";
import { genericPost } from "../../services/Api";
import { SUCCESS_RESULT, ENDPOINTS } from "../../Constants";
import * as XLSX from "xlsx";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

const FailedCriterionList = (props) => {
  const history = useHistory();
  const { component_id, component_name, project_name } = (props.location && props.location.state) || {};
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredList, setFilteredList] = useState([]);

  const gridRef = useRef();

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      sortable: true,
      flex: 1,
      minWidth: 100,
      filter: true,
      resizable: true,
    };
  }, []);

  useEffect(() => {
    if (component_id) {
      loadFailedCriterion();
    } else {
      cogoToast.error("Component ID is required", {});
      history.goBack();
    }
    return () => {};
  }, [component_id]);

  // Filter list based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredList(list);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = list.filter((item) => {
      const firstName = (item.first_name || "").toLowerCase();
      const middleName = (item.middle_name || "").toLowerCase();
      const lastName = (item.last_name || "").toLowerCase();
      const telephone = (item.telephone || "").toLowerCase();
      const campName = (item.camp_name || "").toLowerCase();
      const villageName = (item.village_name || "").toLowerCase();
      const districtName = (item.district_name || "").toLowerCase();
      const userName = (item.user?.name || "").toLowerCase();
      const userEmail = (item.user?.email || "").toLowerCase();

      return (
        firstName.includes(searchLower) ||
        middleName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        telephone.includes(searchLower) ||
        campName.includes(searchLower) ||
        villageName.includes(searchLower) ||
        districtName.includes(searchLower) ||
        userName.includes(searchLower) ||
        userEmail.includes(searchLower)
      );
    });

    setFilteredList(filtered);
  }, [searchTerm, list]);

  const onBtExport = useCallback(() => {
    setIsLoading(true);
    try {
      // Get all row data from the grid (use filtered list if search is active)
      const dataToExport = searchTerm ? filteredList : list;

      if (!dataToExport || dataToExport.length === 0) {
        cogoToast.warn("No data to export", {});
        setIsLoading(false);
        return;
      }

      // Prepare data for export
      const exportData = dataToExport.map((row, index) => {
        const criteriaDetails = row.criteria_details || {};
        return {
          "#": index + 1,
          "First Name": row.first_name || "",
          "Middle Name": row.middle_name || "",
          "Last Name": row.last_name || "",
          "Telephone": row.telephone || "",
          "Camp Name": row.camp_name || "",
          "Village Name": row.village_name || "",
          "District Name": row.district_name || "",
          "Other Name": row.other_name || "",
          "Country Code": row.country_code || "",
          "Score": criteriaDetails.score || "",
          "Can Proceed": criteriaDetails.can_proceed ? "Yes" : "No",
          "Created By": row.user?.name || "",
          "Created By Email": row.user?.email || "",
          "Created At": row.created_at || "",
          "Updated At": row.updated_at || "",
        };
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Failed Criterion");

      // Set column widths
      const columnWidths = [
        { wch: 5 },   // #
        { wch: 15 },  // First Name
        { wch: 15 },  // Middle Name
        { wch: 15 },  // Last Name
        { wch: 15 },  // Telephone
        { wch: 20 },  // Camp Name
        { wch: 20 },  // Village Name
        { wch: 20 },  // District Name
        { wch: 15 },  // Other Name
        { wch: 12 },  // Country Code
        { wch: 10 },  // Score
        { wch: 12 },  // Can Proceed
        { wch: 20 },  // Created By
        { wch: 25 },  // Created By Email
        { wch: 20 },  // Created At
        { wch: 20 },  // Updated At
      ];
      ws["!cols"] = columnWidths;

      // Generate filename with current date
      const fileName = `Failed_Criterion_${component_name || component_id || "Export"}_${new Date().toISOString().split("T")[0]}.xlsx`;

      // Write file
      XLSX.writeFile(wb, fileName);

      cogoToast.success("Data exported to Excel successfully", {});
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      cogoToast.error("Failed to export data to Excel", {});
    } finally {
      setIsLoading(false);
    }
  }, [list, filteredList, searchTerm, component_name, component_id]);

  const loadFailedCriterion = () => {
    setIsLoading(true);

    genericPost(ENDPOINTS.LIST_FAILED_CRITERION, { component_id: component_id })
      .then((res) => {
        setIsLoading(false);
        if (res.status == 200) {
          if (res.data.status == SUCCESS_RESULT) {
            let responseData = res.data.data || [];
            setList(responseData);
            setFilteredList(responseData);
          } else {
            cogoToast.error(res.data.message || "Failed to load failed criterion", {});
            setList([]);
            setFilteredList([]);
          }
        } else {
          cogoToast.error("Failed to load failed criterion", {});
          setList([]);
          setFilteredList([]);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error("Error loading failed criterion:", err);
        cogoToast.error(err?.response?.data?.message || "Something went wrong. Please try again.", {});
        setList([]);
        setFilteredList([]);
      });
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "#",
        filter: false,
        maxWidth: 70,
        sortable: true,
        valueFormatter: function (params) {
          return `${parseInt(params.node.id) + 1}`;
        },
      },
      {
        headerName: "First Name",
        field: "first_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Middle Name",
        field: "middle_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Last Name",
        field: "last_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Telephone",
        field: "telephone",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Camp Name",
        field: "camp_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "Village Name",
        field: "village_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "District Name",
        field: "district_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "Other Name",
        field: "other_name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Country Code",
        field: "country_code",
        sortable: true,
        filter: true,
        maxWidth: 120,
      },
      {
        headerName: "Score",
        field: "criteria_details.score",
        sortable: true,
        filter: true,
        maxWidth: 100,
        valueGetter: (params) => {
          return params.data?.criteria_details?.score || "";
        },
      },
      {
        headerName: "Can Proceed",
        field: "criteria_details.can_proceed",
        sortable: true,
        filter: true,
        maxWidth: 120,
        valueGetter: (params) => {
          const canProceed = params.data?.criteria_details?.can_proceed;
          return canProceed ? "Yes" : "No";
        },
      },
      {
        headerName: "Created By",
        field: "user.name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          return params.data?.user?.name || "";
        },
      },
      {
        headerName: "Email",
        field: "user.email",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.user?.email || "";
        },
      },
      {
        headerName: "Created At",
        field: "created_at",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return "";
          try {
            const date = new Date(params.value);
            return date.toLocaleString();
          } catch (e) {
            return params.value;
          }
        },
      },
    ],
    []
  );

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ marginBottom: "20px" }}>
        Failed Criterion Records [{filteredList.length}]
      </h1>
      <Breadcrumb className="ms-1">
        <BreadcrumbItem>
          <Link to="/">EBMS</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        {project_name && (
          <BreadcrumbItem>
            <Link to="/projects/view">{project_name}</Link>
          </BreadcrumbItem>
        )}
        <BreadcrumbItem active>
          Failed Criterion {component_name ? `- ${component_name}` : ""}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="d-flex justify-content-center my-3">
        {isLoading && <Spinner />}
      </div>

      <div className="card-body" style={{ marginTop: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ flex: 1, minWidth: "300px", maxWidth: "400px" }}>
            <Input
              type="text"
              placeholder="Search by name, telephone, location, or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ddd",
              }}
            />
          </div>
          <Button
            color="primary"
            onClick={onBtExport}
            disabled={isLoading || (!searchTerm ? list.length === 0 : filteredList.length === 0)}
            style={{
              padding: "10px 20px",
              borderRadius: "5px",
              fontWeight: "500",
            }}
          >
            {isLoading ? "Exporting..." : "Export To Excel"}
          </Button>
        </div>

        <div className="ag-theme-alpine" style={{ height: "600px", width: "100%" }}>
          <AgGridReact
            suppressMenuHide={true}
            ref={gridRef}
            onGridReady={(params) => {
              params.api.sizeColumnsToFit();
            }}
            rowData={filteredList}
            domLayout="normal"
            animateRows={true}
            pagination={true}
            paginationPageSize={50}
            defaultColDef={defaultColDef}
            resizable={true}
            enableCellTextSelection={true}
            suppressCellSelection={true}
            columnDefs={columnDefs}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FailedCriterionList;
